import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline } from '@mui/material';
import api from '../services/api';
import { useAuth } from './AuthContext';
import { createContactTelTheme, type ThemeMode } from '../theme';

interface ThemeModeContextValue {
  mode: ThemeMode;
  setMode: (mode: ThemeMode) => void;
  toggleMode: () => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue | undefined>(undefined);

const getStoredTheme = (): ThemeMode => {
  if (typeof window === 'undefined') return 'dark';
  const stored = localStorage.getItem('theme');
  if (stored === 'light' || stored === 'dark') {
    return stored as ThemeMode;
  }
  return 'dark';
};

const persistTheme = (value: ThemeMode) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('theme', value);
  }
};

/**
 * Provides theme mode state and persistence.
 */
export const ThemeModeProvider = ({ children }: { children: ReactNode }) => {
  const { user, isAuthenticated } = useAuth();
  const [mode, setModeState] = useState<ThemeMode>(getStoredTheme);

  useEffect(() => {
    if (isAuthenticated && user?.theme) {
      setModeState((currentMode) => {
        if (currentMode === user.theme) {
          return currentMode;
        }
        persistTheme(user.theme as ThemeMode);
        return user.theme as ThemeMode;
      });
    }
  }, [isAuthenticated, user?.theme]);

  useEffect(() => {
    if (typeof document !== 'undefined') {
      document.body.dataset.theme = mode;
    }
  }, [mode]);

  const setMode = useCallback(
    (nextMode: ThemeMode) => {
      setModeState(nextMode);
      persistTheme(nextMode);
      if (isAuthenticated) {
        api.patch('/api/account/preferences/', { theme: nextMode }).catch((error) => {
          console.error('Failed to save theme preference:', error);
        });
      }
    },
    [isAuthenticated],
  );

  const toggleMode = useCallback(() => {
    setMode(mode === 'dark' ? 'light' : 'dark');
  }, [mode, setMode]);

  const theme = useMemo(() => createContactTelTheme(mode), [mode]);

  return (
    <ThemeModeContext.Provider value={{ mode, setMode, toggleMode }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
};

/**
 * Access theme mode and actions.
 */
export const useThemeMode = () => {
  const context = useContext(ThemeModeContext);
  if (!context) {
    throw new Error('useThemeMode must be used within ThemeModeProvider');
  }
  return context;
};
